import { Fragment, useEffect, useRef, useState } from "react";
import PatientModuleLayout from "../../../components/patients/layout/PatientModuleLayout";
import {
    AdjustmentsHorizontalIcon,
    LockClosedIcon,
    MagnifyingGlassIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { ScheduleTable } from "./components/ScheduleTable";
import api from "../../../api";
import { useParams } from "react-router-dom";
import { DialogMenu } from "../components/DialogMenu";
import { ScheduleBlockForm } from "./components/ScheduleBlockForm";
import toast, { Toaster } from "react-hot-toast";
import { ScheduleForm } from "./components/ScheduleForm";
import SidebarToggleButton from "../../../components/layout/SidebarToggleButton";

export function DoctorAgenda() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [schedules, setSchedules] = useState([]);
    const [schedulesBlock, setSchedulesBlock] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBlockDate, setSelectedBlockDate] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const dialogRef = useRef(null);
    const editDialogRef = useRef(null);
    const editScheduleDialogRef = useRef(null);
    const { idSchedule } = useParams();
    const { doctor } = useParams();

    const fetchSchedule = async () => {
        try {
            setLoading(true);
            const data = await api.schedules.getOne(idSchedule);
            setSchedules([data]);
        } catch (error) {
            console.error("Error al obtener los schedules:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBlockSchedule = async () => {
        try {
            setLoading(true);
            const data = await api.schedulesBlock.getMulti(
                null,
                null,
                null,
                null,
                { schedule_id: idSchedule }
            );
            setSchedulesBlock(data);
        } catch (error) {
            console.error("Error al obtener las fechas bloqueadas:", error);
        } finally {
            setLoading(false);
        }
    };

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };

    useEffect(() => {
        fetchSchedule();
        fetchBlockSchedule();
    }, [idSchedule]);

    const pages = [
        { name: "Agendamientos", href: "/schedule", current: false },
        { name: "Agenda", href: "/schedule/agenda", current: false },
        {
            name: `${doctor}`,
            href: `/schedule/agenda/${idSchedule}`,
            current: true,
        },
    ];

    const handleRowClick = (appt) => {
        setSelectedSchedule(appt);
        editScheduleDialogRef.current?.showModal();
    };

    const handleRowClickBlock = (appt) => {
        setSelectedBlockDate(appt);
        editDialogRef.current?.showModal();
    };

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen="Agendamiento"
            header={
                <LocalHeader
                    dialogRef={dialogRef}
                    setSidebarOpen={setSidebarOpen}
                />
            }
        >
                <main className="p-6 sm:p-10 flex flex-col">
                    <Breadcrumbs pages={pages} />
                    <h2 className="text-4xl font-bold mb-10 mt-4 dark:text-gray-200 ">
                        {`${doctor}`}
                    </h2>
                    <div className="rounded-lg overflow-hidden shadow-lg border-b border-gray-200 dark:border-gray-700">
                        <ScheduleTable
                            schedules={schedules}
                            loading={loading}
                            onRowClick={handleRowClick}
                        />
                    </div>
                    <h3 className="text-3xl font-bold mb-4 mt-8 dark:text-gray-200 ">
                        Permisos
                    </h3>
                    <div className="rounded-lg overflow-hidden shadow-lg border-b border-gray-200 dark:border-gray-700">
                        <ScheduleBlockTable
                            data={schedulesBlock}
                            loading={loading}
                            onRowClick={handleRowClickBlock}
                        />
                    </div>
                    <DialogMenu dialogRef={dialogRef}>
                        <ScheduleBlockForm
                            dialogRef={dialogRef}
                            idSchedule={idSchedule}
                            notify={notify}
                            onSave={fetchBlockSchedule}
                        />
                    </DialogMenu>
                    <DialogMenu dialogRef={editDialogRef}>
                        <ScheduleBlockForm
                            dialogRef={editDialogRef}
                            idSchedule={idSchedule}
                            initialBlockDate={selectedBlockDate}
                            notify={notify}
                            onSave={fetchBlockSchedule}
                        />
                    </DialogMenu>
                    <DialogMenu dialogRef={editScheduleDialogRef}>
                        <ScheduleForm
                            dialogRef={editScheduleDialogRef}
                            schedule={selectedSchedule}
                            onSave={fetchSchedule}
                            notify={notify}
                        />
                    </DialogMenu>
                </main>
            <Toaster />
        </PatientModuleLayout>
    );
}

const LocalHeader = ({ dialogRef, setSidebarOpen }) => {
    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
          <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <SidebarToggleButton onClick={() => setSidebarOpen(true)} />
                <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <label htmlFor="search">
                        <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary" />
                    </label>
                    <input
                        type="search"
                        name="search"
                        id="search"
                        className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                        placeholder="Buscar"
                    />
                </div>
            </div>
            <div className="hidden md:flex justify-end gap-3">
                <div className="flex gap-2">
                    <button
                        className="items-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex"
                        onClick={() => dialogRef.current?.showModal()}
                    >
                        <LockClosedIcon className="h-5 w-5" />
                        <span>Bloquear fechas</span>
                    </button>
                </div>
            </div>
            <div className="md:hidden flex justify-end">
                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700">
                        <AdjustmentsHorizontalIcon className="h-6 w-6" />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-slate-200 bg-white p-4 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900 flex flex-col gap-3">
                            <button
                                className="items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex w-full justify-center"
                                onClick={() => dialogRef.current?.showModal()}
                            >
                                <PlusIcon className="h-5 w-5" />
                                <span>Nuevo Horario</span>
                            </button>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
          </div>
        </div>
    );
};

const ScheduleBlockTable = ({ data, loading, onRowClick }) => {
    return (
        <div className=" border-gray-200 dark:border-gray-700 max-h-[70dvh] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm table-fixed">
                <thead className="bg-white dark:bg-gray-900">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-semiblod text-gray-500 dark:text-gray-300 tracking-wider">
                            Fecha de inicio
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semiblod text-gray-500 dark:text-gray-300 tracking-wider">
                            Fecha de finalización
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semiblod text-gray-500 dark:text-gray-300 tracking-wider w-6/12">
                            Motivo
                        </th>
                    </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400 font-medium">
                    {loading ? (
                        <tr>
                            <td
                                colSpan={9}
                                className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                            >
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={3}
                                className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                            >
                                No hay permisos agendados
                            </td>
                        </tr>
                    ) : (
                        data.map((item, idx) => (
                            <tr
                                key={idx}
                                className={`${
                                    idx % 2 === 0
                                        ? "bg-white dark:bg-gray-900"
                                        : "bg-gray-50 dark:bg-gray-800"
                                } hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer`}
                                onClick={() => onRowClick?.(item)}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {item.startDate}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {item.endDate}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {item.reason}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};
